// The outline of a pull request description: its headings as a nested list
// the Overview shows beside it, and which of them is being read.

/** A heading of the rendered description: `h1` is level 1. */
export interface Heading {
  level: number;
  text: string;
}

/** One line of the outline. */
export interface OutlineEntry {
  /** The heading's place among all the headings given (in document order). */
  index: number;
  /** How far it is indented: 0 for the outermost headings. */
  depth: number;
  text: string;
}

/** Fewer headings than this make no outline. */
export const OUTLINE_MIN = 2;

/** Nesting levels shown; headings below the third are left out. */
const MAX_DEPTH = 3;

/** A heading's text as one line. */
export function headingText(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim();
}

/**
 * The outline of `headings` (in document order). A heading is nested under
 * the nearest earlier heading of a lower level, one step deeper whatever
 * levels it skips; headings nested deeper than three steps and headings
 * without text are left out. Empty when fewer than OUTLINE_MIN remain.
 */
export function outline(headings: readonly Heading[]): OutlineEntry[] {
  const out: OutlineEntry[] = [];
  const open: number[] = [];
  headings.forEach((h, index) => {
    const text = headingText(h.text);
    if (!text) return;
    while (open.length && open[open.length - 1] >= h.level) open.pop();
    const depth = open.length;
    open.push(h.level);
    if (depth < MAX_DEPTH) out.push({ index, depth, text });
  });
  return out.length >= OUTLINE_MIN ? out : [];
}

/**
 * The entry being read, given whether each entry's heading is above the
 * reading line (`aboveLine`): the last one that is, or the first while
 * none is; -1 once the description itself has scrolled past the line
 * (`past`), and without entries.
 */
export function readingEntry(above: readonly boolean[], past: boolean): number {
  if (past || !above.length) return -1;
  return Math.max(0, above.lastIndexOf(true));
}

/** An element's box, as `getBoundingClientRect` (or an IntersectionObserver entry) gives it. */
export interface Box {
  top: number;
  bottom: number;
  width: number;
  height: number;
}

/**
 * True when an element's top has passed the reading line `line` (pixels
 * from the viewport's top). An element with no layout box (inside a closed
 * `<details>`, or hidden) never has.
 */
export function aboveLine(box: Box, line: number): boolean {
  if (!box.width && !box.height) return false;
  return box.top <= line;
}

const HEADINGS = 'h1, h2, h3, h4, h5, h6';

/** The heading elements under `root`, in document order: `headingsOf` reads the same ones from HTML. */
export function headingElements(root: ParentNode): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(HEADINGS)];
}

/** The headings of rendered (sanitized) HTML, in document order. */
export function headingsOf(html: string): Heading[] {
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  return headingElements(tpl.content).map((el) => ({ level: Number(el.tagName[1]), text: headingText(el.textContent ?? '') }));
}

/** Open every `<details>` around `el`, so it has a box to scroll to. */
export function revealHeading(el: Element) {
  for (let d = el.parentElement?.closest('details'); d; d = d.parentElement?.closest('details')) d.open = true;
}
